import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pencil } from "lucide-react";
import { useEditMode } from "@/contexts/CmsContext";
import {
  ensureImageLoaded,
  subscribeAllImages,
  getCachedImage,
  resolveUrl,
} from "@/lib/slotCache";
import { slotFromImageElement, legacySlotFromImageElement, slotFromBgElement, legacySlotFromBgElement, extractBgUrl } from "@/lib/autoSlot";
import { ImageEditModal } from "@/components/cms/CmsImage";

// ────────────────────────────────────────────────────────────────────────
// AutoImageEditor — global engine that makes every  loading="lazy" decoding="async"<img> and inline
// background-image on the site editable from "Edit Images" mode without
// touching individual components.
//
// • Scans the DOM (MutationObserver) for  loading="lazy" decoding="async"<img> and inline `style.background-image`.
// • Generates a stable slot id from each URL (`auto.<tag>.<hash>`).
// • Applies persisted overrides on first paint and after every navigation.
// • Renders a fixed-position EDIT pencil on top of every editable image
//   while in image edit mode.
//
// Opt-out: add `data-no-cms-edit="true"` on the element (or any ancestor)
// to exclude an image. Components that already use <EditableImage> are
// auto-skipped via the `[data-cms-image-slot]` marker on its wrapper span.
// ────────────────────────────────────────────────────────────────────────

const MIN_SIZE = 64; // ignore icons / tiny ornamental images
const VIEWPORT_BUFFER = 400; // also render pencils slightly off-screen so they're ready by the time the user scrolls

const isInsideManagedImage = (el) =>
  !!el.closest && el.closest("[data-cms-image-slot]");
const isOptedOut = (el) =>
  !!el.closest && el.closest('[data-no-cms-edit="true"]');
const isLeaflet = (el) =>
  !!el.closest && el.closest(".leaflet-container");
const isSkipped = (el) =>
  isInsideManagedImage(el) || isOptedOut(el) || isLeaflet(el);

// Visible only if it has dimensions and isn't `display: none`, `visibility: hidden`,
// `opacity: 0`, or under any ancestor that's hidden or aria-hidden="true".
const isVisible = (rect, el) => {
  if (rect.width < MIN_SIZE || rect.height < MIN_SIZE) return false;
  if (rect.bottom < -VIEWPORT_BUFFER) return false;
  if (rect.top > window.innerHeight + VIEWPORT_BUFFER) return false;
  // Walk up the tree checking opacity / visibility / display / aria-hidden
  let node = el;
  while (node && node !== document.body) {
    if (node.getAttribute && node.getAttribute("aria-hidden") === "true") return false;
    const cs = window.getComputedStyle(node);
    if (cs.display === "none") return false;
    if (cs.visibility === "hidden") return false;
    if (parseFloat(cs.opacity) === 0) return false;
    node = node.parentElement;
  }
  return true;
};

// Apply persisted overrides directly on the DOM so the user sees the saved
// image even when components are pure <img src=... loading="lazy" decoding="async"> with no React state.
//
// LOOKUP ORDER: each <img>/bg element generates a primary (URL-independent,
// env-stable) slot id AND a legacy (URL-fingerprinted) slot id. We consult
// the cache with the primary id first; when that's empty we fall back to
// the legacy id. This means edits saved against the pre-refactor slot
// scheme are still applied without requiring any destructive migration of
// the `image_slots` collection. New edits always persist against the
// primary id, so the legacy fallback fades out naturally as content is
// re-edited.
const applyOverrides = () => {
  //  loading="lazy" decoding="async"<img>
  document.querySelectorAll("img").forEach((img) => {
    if (isSkipped(img)) return;
    const slot = slotFromImageElement(img);
    if (!slot) return;
    let data = getCachedImage(slot);
    if (!data || !data.url || data.cleared) {
      // Legacy fallback — preserves pre-refactor edits.
      const legacy = legacySlotFromImageElement(img);
      if (legacy) {
        const legacyData = getCachedImage(legacy);
        if (legacyData && legacyData.url && !legacyData.cleared) {
          data = legacyData;
        }
      }
    }
    // ── <picture> override ────────────────────────────────────────────
    // When an <img> is wrapped by <OptimizedImage>, it lives inside a
    // <picture> with <source srcSet="…"> siblings. Browsers consult the
    // <source> tags FIRST and only fall back to <img src> when no source
    // matches. Just rewriting img.src has no visible effect in that
    // case — the user sees the original photo even though the slot now
    // points elsewhere.
    //
    // Strategy: when a CMS override is active, strip every <source>
    // sibling's srcSet so the browser is forced to honour <img src>.
    // When the override is cleared (data === null or cleared), restore
    // the original srcSet we stashed away on dataset.cmsSavedSrcset.
    const picture = img.parentElement && img.parentElement.tagName === "PICTURE"
      ? img.parentElement
      : null;
    const sources = picture ? picture.querySelectorAll("source") : null;
    if (data && data.url && !data.cleared) {
      const resolved = resolveUrl(data.url);
      if (resolved && img.src !== resolved) {
        if (!img.dataset.cmsSrc) img.dataset.cmsSrc = img.src;
        img.src = resolved;
        // Also wipe srcset on the <img> itself so it can't outvote src.
        if (img.srcset) {
          if (!img.dataset.cmsSavedSrcsetImg) img.dataset.cmsSavedSrcsetImg = img.srcset;
          img.srcset = "";
        }
      }
      if (sources) {
        sources.forEach((s) => {
          if (s.srcset && !s.dataset.cmsSavedSrcset) {
            s.dataset.cmsSavedSrcset = s.srcset;
            s.srcset = "";
          } else if (!s.srcset && !s.dataset.cmsSavedSrcset) {
            // Already empty — mark it so we don't try to restore.
            s.dataset.cmsSavedSrcset = "";
          }
        });
      }
    } else {
      if (img.dataset.cmsSrc && img.src !== img.dataset.cmsSrc) {
        img.src = img.dataset.cmsSrc;
      }
      if (img.dataset.cmsSavedSrcsetImg && img.srcset !== img.dataset.cmsSavedSrcsetImg) {
        img.srcset = img.dataset.cmsSavedSrcsetImg;
      }
      if (sources) {
        sources.forEach((s) => {
          if (s.dataset.cmsSavedSrcset && s.srcset !== s.dataset.cmsSavedSrcset) {
            s.srcset = s.dataset.cmsSavedSrcset;
          }
        });
      }
    }
  });
  // Inline background-image
  document.querySelectorAll('[style*="background-image"]').forEach((el) => {
    if (isOptedOut(el)) return;
    const originalBg = el.dataset.cmsBgSrc || extractBgUrl(el.style.backgroundImage);
    if (!originalBg) return;
    const slot = slotFromBgElement(el, originalBg);
    if (!slot) return;
    let data = getCachedImage(slot);
    if (!data || !data.url || data.cleared) {
      const legacy = legacySlotFromBgElement(el, originalBg);
      if (legacy) {
        const legacyData = getCachedImage(legacy);
        if (legacyData && legacyData.url && !legacyData.cleared) {
          data = legacyData;
        }
      }
    }
    if (data && data.url && !data.cleared) {
      const resolved = resolveUrl(data.url);
      if (!resolved) return;
      const wanted = `url("${resolved}")`;
      if (el.style.backgroundImage !== wanted) {
        if (!el.dataset.cmsBgSrc) el.dataset.cmsBgSrc = originalBg;
        el.style.backgroundImage = wanted;
      }
    } else if (el.dataset.cmsBgSrc) {
      const wanted = `url("${el.dataset.cmsBgSrc}")`;
      if (el.style.backgroundImage !== wanted) {
        el.style.backgroundImage = wanted;
      }
    }
  });
};

const AutoImageEditor = () => {
  const { imageEditMode, pendingEditorSlot, consumePendingEditorSlot } = useEditMode();
  const [pencils, setPencils] = useState([]); // [{slot, top, left, ...}]
  const [editing, setEditing] = useState(null);
  const tickRef = useRef(null);
  const seenRectsRef = useRef(new Map());

  // Build the list of pencils to render based on current DOM state.
  const rescanPencils = useCallback(() => {
    if (!imageEditMode) {
      setPencils([]);
      return;
    }
    const next = [];
    const seen = new Set();

    document.querySelectorAll("img").forEach((img) => {
      if (isSkipped(img)) return;
      const rect = img.getBoundingClientRect();
      if (!isVisible(rect, img)) return;
      const slot = slotFromImageElement(img);
      if (!slot || seen.has(slot)) return;
      seen.add(slot);
      next.push({
        slot,
        top: rect.top + rect.height / 2,
        left: rect.left + rect.width / 2,
        type: "img",
        currentUrl: img.src,
        alt: img.alt || "",
      });
    });

    document.querySelectorAll('[style*="background-image"]').forEach((el) => {
      if (isOptedOut(el)) return;
      const rect = el.getBoundingClientRect();
      if (!isVisible(rect, el)) return;
      const originalBg = el.dataset.cmsBgSrc || extractBgUrl(el.style.backgroundImage);
      if (!originalBg) return;
      const slot = slotFromBgElement(el, originalBg);
      if (!slot || seen.has(slot)) return;
      seen.add(slot);
      next.push({
        slot,
        top: rect.top + rect.height / 2,
        left: rect.left + rect.width / 2,
        type: "bg",
        currentUrl: extractBgUrl(el.style.backgroundImage),
        alt: "",
      });
    });

    setPencils(next);
  }, [imageEditMode]);

  const tick = useCallback(() => {
    if (tickRef.current) cancelAnimationFrame(tickRef.current);
    tickRef.current = requestAnimationFrame(() => {
      applyOverrides();
      rescanPencils();
    });
  }, [rescanPencils]);

  // ── Initial load + slot-cache subscription ────────────────────────────
  useEffect(() => {
    let active = true;
    ensureImageLoaded().then(() => {
      if (!active) return;
      applyOverrides();
      rescanPencils();
    });
    const unsubscribe = subscribeAllImages(() => {
      if (!active) return;
      tick();
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [rescanPencils, tick]);

  // ── MutationObserver: new images, dynamically loaded content, route changes ──
  useEffect(() => {
    const isOwnButton = (n) =>
      n && n.nodeType === 1 && n.classList && n.classList.contains("auto-image-edit-btn");
    const observer = new MutationObserver((mutations) => {
      let interesting = false;
      for (const m of mutations) {
        if (m.type === "childList") {
          let onlyOurs = true;
          for (const n of m.addedNodes) if (!isOwnButton(n)) { onlyOurs = false; break; }
          if (onlyOurs) {
            for (const n of m.removedNodes) if (!isOwnButton(n)) { onlyOurs = false; break; }
          }
          if (!onlyOurs) { interesting = true; break; }
        } else if (m.type === "attributes") {
          if (isOwnButton(m.target)) continue;
          interesting = true;
          break;
        }
      }
      if (interesting) tick();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src", "style"],
    });
    return () => observer.disconnect();
  }, [tick]);

  // ── Recompute pencil positions on scroll/resize while in edit mode ────
  useEffect(() => {
    if (!imageEditMode) {
      setPencils([]);
      return undefined;
    }
    rescanPencils();
    const onUpdate = () => tick();
    window.addEventListener("scroll", onUpdate, { passive: true, capture: true });
    window.addEventListener("resize", onUpdate);
    return () => {
      window.removeEventListener("scroll", onUpdate, { capture: true });
      window.removeEventListener("resize", onUpdate);
    };
  }, [imageEditMode, rescanPencils, tick]);

  // Periodic redraw — covers CSS animations, transitions, and ken-burns scale
  // changes that don't fire mutation events.
  useEffect(() => {
    if (!imageEditMode) return undefined;
    const id = window.setInterval(() => rescanPencils(), 600);
    return () => window.clearInterval(id);
  }, [imageEditMode, rescanPencils]);

  // ── Deep-link → immediate edit ────────────────────────────────────────
  //
  // When PreviewHighlighter (or any other surface) calls
  // `openImageEditor(slot, {currentUrl, alt})`, the CMS context stores
  // the payload in `pendingEditorSlot`. Here we watch for it, resolve
  // the current live URL+alt from the DOM (in case the caller only
  // knew the slot id), then open the ImageEditModal. Consumed once so
  // navigating back to the page without the query param doesn't
  // re-open the editor unexpectedly.
  useEffect(() => {
    if (!pendingEditorSlot) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // ~7.5s at 250ms cadence

    const findLiveImageForSlot = (slotId) => {
      // Match order mirrors PreviewHighlighter's 3-tier lookup so
      // both surfaces resolve to the same DOM node for a given slot.
      const esc = window.CSS && window.CSS.escape ? window.CSS.escape(slotId) : slotId.replace(/"/g, '\\"');
      const direct = document.querySelector(`img[data-cms-slot="${esc}"]`);
      if (direct) return direct;
      const wrap = document.querySelector(`[data-cms-image-slot="${esc}"]`);
      if (wrap) return wrap.querySelector("img") || wrap;
      const m = slotId.match(/^cms\.(.+)\.([^.]+)$/);
      if (m) {
        const [, scope, role] = m;
        const scopeEsc = window.CSS && window.CSS.escape ? window.CSS.escape(scope) : scope;
        const roleEsc  = window.CSS && window.CSS.escape ? window.CSS.escape(role)  : role;
        const nodes = document.querySelectorAll(`[data-cms-role="${roleEsc}"]`);
        for (const n of nodes) {
          if (n.closest(`[data-cms-scope="${scopeEsc}"]`)) return n;
        }
      }
      return null;
    };

    const tryOpen = () => {
      if (cancelled) return;
      attempts += 1;
      const img = findLiveImageForSlot(pendingEditorSlot.slot);
      const currentUrl =
        (img && (img.currentSrc || img.getAttribute("src"))) ||
        pendingEditorSlot.currentUrl || "";
      const alt =
        (img && img.getAttribute && img.getAttribute("alt")) ||
        pendingEditorSlot.alt || "";
      // If the caller already resolved the DOM lookup (e.g.
      // PreviewHighlighter finished its own polling before dispatching)
      // then we trust that result and open the modal immediately even
      // when no <img> was found — no point running the same expensive
      // polling loop twice. Otherwise, poll for lazy hydration.
      const alreadyResolved = !!pendingEditorSlot.resolved;
      if (!currentUrl && !img && !alreadyResolved && attempts < MAX_ATTEMPTS) {
        window.setTimeout(tryOpen, 250);
        return;
      }
      setEditing({
        slot: pendingEditorSlot.slot,
        currentUrl,
        alt,
      });
      consumePendingEditorSlot();
    };

    // Slight delay so the first scan of pencils + resolveOverrides
    // has a chance to populate before we probe.
    const t = window.setTimeout(tryOpen, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // Intentionally NOT depending on `pencils`/`imageEditMode` — those
    // change every ~600ms and would restart the polling loop. The
    // effect only needs to fire when a NEW pending slot arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEditorSlot, consumePendingEditorSlot]);

  if (!imageEditMode && !editing) return null;

  return createPortal(
    <>
      {imageEditMode &&
        pencils.map(({ slot, top, left, currentUrl, alt }) => (
          <button
            key={slot}
            type="button"
            className="auto-image-edit-btn"
            style={{ top, left }}
            data-edit-allow="true"
            data-testid={`auto-edit-btn-${slot}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setEditing({ slot, currentUrl, alt });
            }}
            title={`Edit ${slot}`}
          >
            <Pencil size={12} strokeWidth={1.8} />
            <span>Edit</span>
          </button>
        ))}
      {editing && (
        <ImageEditModal
          slot={editing.slot}
          currentUrl={editing.currentUrl}
          currentAlt={editing.alt}
          onClose={() => {
            setEditing(null);
            tick();
          }}
        />
      )}
    </>,
    document.body
  );
};

export default AutoImageEditor;
